import { useAppConfig } from "../lib/AppConfigContext";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { 
  LayoutDashboard, 
  Building2, 
  Vote, 
  LogOut,
  Menu,
  X,
  Shield,
  Settings,
  UserCog,
  Link2,
  AlertTriangle,
  Wrench,
  FileText
} from "lucide-react";
import { useState, useEffect, useCallback } from "react";
import { Button } from "./ui/button";
import adminApi from "../lib/adminApi";
import { LanguageSelector } from "./LanguageSelector";
import { LegalFooterDark } from "./LegalFooter";

export const AdminLayout = ({ children }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [balanceAlert, setBalanceAlert] = useState(null);
  const adminUsername = localStorage.getItem("admin_username") || "Admin";
  const adminName = localStorage.getItem("admin_name") || "Admin";

  const checkBalance = useCallback(async () => {
    try {
      const res = await adminApi.get("/admin/blockchain/balance-status");
      setBalanceAlert(res.data.alert ? res.data : null);
    } catch {
      // Silently ignore if not configured
    }
  }, []);

  useEffect(() => {
    checkBalance();
    const interval = setInterval(checkBalance, 120000); // Every 2 min
    return () => clearInterval(interval);
  }, [checkBalance]);

  const handleLogout = async () => {
    try {
      const adminApi = (await import("../lib/adminApi")).default;
      await adminApi.post("/admin/logout").catch(() => {});
    } catch (_) {}
    localStorage.removeItem("admin_username");
    localStorage.removeItem("admin_name");
    navigate("/admin/login");
  };

  const { appName } = useAppConfig();

  const navItems = [
    { path: "/admin/dashboard", icon: LayoutDashboard, label: t('admin.dashboard') },
    { path: "/admin/entities", icon: Building2, label: t('admin.entities') },
    { path: "/admin/consultes", icon: Vote, label: t('admin.consultes') },
    { path: "/admin/scrutiny-requests", icon: Shield, label: t('nav.scrutinies') },
    { path: "/admin/users", icon: UserCog, label: t('admin.administrators') },
    { path: "/admin/config", icon: Settings, label: t('nav.settings') },
    { path: "/admin/blockchain", icon: Link2, label: t('blockchain.title') },
    { path: "/admin/audit", icon: FileText, label: "Auditoria" },
    { path: "/admin/maintenance", icon: Wrench, label: t('maintenance.sidebarLabel') },
  ];

  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + "/");

  return (
    <div className="min-h-screen bg-slate-900">
      {/* Mobile header */}
      <header className="lg:hidden sticky top-0 z-40 bg-slate-800 border-b border-slate-700">
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="text-slate-300 hover:text-white hover:bg-slate-700"
              data-testid="admin-mobile-menu-toggle"
            >
              {sidebarOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </Button>
            <span className="font-heading font-bold text-white">{t('admin.adminPanel')}</span>
          </div>
          <div className="flex items-center gap-2 text-sm text-slate-400">
            <LanguageSelector dark />
            <Shield className="h-4 w-4 text-amber-500" />
            <span>{adminUsername}</span>
          </div>
        </div>
      </header>

      <div className="flex">
        {/* Sidebar */}
        <aside
          className={`
            fixed lg:sticky top-0 left-0 z-30 h-screen w-64 bg-slate-800 border-r border-slate-700
            transform transition-transform duration-200 ease-in-out
            ${sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}
          `}
        >
          <div className="flex flex-col h-full">
            {/* Logo */}
            <div className="hidden lg:flex items-center gap-3 px-6 py-5 border-b border-slate-700">
              <div className="w-10 h-10 bg-amber-500 rounded-lg flex items-center justify-center">
                <Shield className="h-5 w-5 text-white" />
              </div>
              <div>
                <h1 className="font-heading font-bold text-white">{appName}</h1>
                <p className="text-xs text-slate-400">{t('admin.panelAdmin')}</p>
              </div>
            </div>

            {/* Navigation */}
            <nav className="flex-1 px-3 py-4 space-y-1">
              {navItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setSidebarOpen(false)}
                  className={`
                    flex items-center gap-3 px-4 py-3 rounded-md transition-colors
                    ${isActive(item.path) 
                      ? "bg-amber-500/20 text-amber-400" 
                      : "text-slate-400 hover:bg-slate-700 hover:text-white"}
                  `}
                  data-testid={`admin-nav-${item.label.toLowerCase()}`}
                >
                  <item.icon className="h-5 w-5" />
                  <span>{item.label}</span>
                </Link>
              ))}
            </nav>

            {/* User section */}
            <div className="border-t border-slate-700 p-4">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-slate-700 rounded-full flex items-center justify-center">
                  <Shield className="h-5 w-5 text-amber-500" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-white truncate">{adminName}</p>
                  <p className="text-xs text-slate-400">{t('admin.administrator')}</p>
                </div>
              </div>
              <div className="mb-2">
                <LanguageSelector dark />
              </div>
              <Button
                variant="ghost"
                className="w-full justify-start text-slate-400 hover:text-red-400 hover:bg-red-500/10"
                onClick={handleLogout}
                data-testid="admin-logout-btn"
              >
                <LogOut className="h-4 w-4 mr-2" />
                {t('nav.logout')}
              </Button>
            </div>
          </div>
        </aside>

        {/* Overlay */}
        {sidebarOpen && (
          <div
            className="fixed inset-0 bg-black/50 z-20 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Main content */}
        <main className="flex-1 min-h-screen">
          {balanceAlert && (
            <div className="bg-red-900/80 border-b border-red-700 px-4 py-3 flex items-center gap-3" data-testid="balance-alert-banner">
              <AlertTriangle className="h-5 w-5 text-red-400 shrink-0" />
              <p className="text-sm text-red-200 flex-1">
                <span className="font-semibold">{t('blockchain.lowBalanceAlert')}: </span>
                {balanceAlert.wallet_label} — <span className="font-mono">{balanceAlert.balance?.toFixed(6)} SOL</span>
                <span className="text-red-400"> ({t('blockchain.minThreshold')}: {balanceAlert.threshold} SOL)</span>
              </p>
              <Link to="/admin/blockchain" className="text-xs bg-red-700 hover:bg-red-600 text-white px-3 py-1 rounded font-medium whitespace-nowrap">
                {t('blockchain.goToWallets')}
              </Link>
            </div>
          )}
          {children}
          <LegalFooterDark />
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
